/**
 * StatusActions — Sprint R3
 *
 * Universal action buttons for status transitions.
 * Renders the correct transition buttons based on current status + user role.
 * Calls POST /api/v1/aif/status/transition/{table}/{id} on click.
 *
 * Follows STATUS_LIFECYCLE.md §5 allowed transitions.
 *
 * Props:
 *   eventTable    — 'aif_farm_tasks' | 'aif_field_activities'
 *   eventId       — UUID string
 *   currentStatus — 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'POSTED' | 'ARCHIVED'
 *   userRole      — 'owner' | 'manager' | 'worker' | 'advisor'
 *   token         — JWT bearer token
 *   onTransition  — callback(newStatus) after successful transition
 *   disabled      — optional disable flag
 */

import { useState } from 'react';
import { apiClient } from '../lib/apiClient';

// Which transitions each role may trigger, per current status
// (subset of the allowed graph from STATUS_LIFECYCLE.md)
const ROLE_TRANSITIONS = {
  DRAFT: {
    owner:   ['SUBMITTED', 'ARCHIVED'],
    manager: ['SUBMITTED', 'ARCHIVED'],
    worker:  ['SUBMITTED'],
    advisor: [],
  },
  SUBMITTED: {
    owner:   ['APPROVED', 'DRAFT'],
    manager: ['APPROVED', 'DRAFT'],
    worker:  ['APPROVED'],
    advisor: [],
  },
  APPROVED: {
    owner:   ['POSTED', 'SUBMITTED'],
    manager: ['POSTED', 'SUBMITTED'],
    worker:  [],
    advisor: [],
  },
  POSTED: {
    owner:   [],   // GoBD-immutable — no UI transitions
    manager: [],
    worker:  [],
    advisor: [],
  },
  ARCHIVED: {
    owner:   [],
    manager: [],
    worker:  [],
    advisor: [],
  },
};

const BUTTON_CONFIG = {
  SUBMITTED: { label: 'Einreichen',   variant: 'primary' },
  APPROVED:  { label: 'Abschließen',  variant: 'success' },
  POSTED:    { label: 'Freigeben',    variant: 'lock'    },
  DRAFT:     { label: 'Zurücknehmen', variant: 'ghost'   },
  ARCHIVED:  { label: 'Archivieren',  variant: 'danger'  },
};

const VARIANT_CLASSES = {
  primary: 'bg-blue-500 hover:bg-blue-600 text-white',
  success: 'bg-green-500 hover:bg-green-600 text-white',
  lock:    'bg-emerald-700 hover:bg-emerald-800 text-white',
  ghost:   'bg-transparent border border-gray-400 text-[var(--aif-text-secondary)] hover:bg-gray-100',
  danger:  'bg-red-500 hover:bg-red-600 text-white',
};

export default function StatusActions({
  eventTable,
  eventId,
  currentStatus,
  userRole,
  token,
  onTransition,
  disabled = false,
}) {
  const [loading, setLoading] = useState(null); // null | target status
  const [error, setError] = useState(null);

  const allowed = ROLE_TRANSITIONS[currentStatus]?.[userRole] ?? [];

  if (allowed.length === 0) return null;

  const handleTransition = async (toStatus) => {
    setLoading(toStatus);
    setError(null);
    try {
      await apiClient.post(`/api/v1/aif/status/transition/${eventTable}/${eventId}`, { to_status: toStatus }, { queueOnOffline: true }); // TODO(5b): confirm 3WM — status transitions on shared entities
      onTransition?.(toStatus);
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="status-actions">
      {allowed.map((target) => {
        const cfg = BUTTON_CONFIG[target] ?? { label: target, variant: 'primary' };
        return (
          <button
            key={target}
            onClick={() => handleTransition(target)}
            disabled={disabled || loading !== null}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition
              ${VARIANT_CLASSES[cfg.variant]}
              disabled:opacity-50 disabled:cursor-not-allowed`}
            data-testid={`status-action-${target.toLowerCase()}`}
            data-status-action={target}
          >
            {loading === target ? '…' : cfg.label}
          </button>
        );
      })}
      {error && (
        <span className="text-xs text-red-500" data-testid="status-action-error">
          {error}
        </span>
      )}
    </div>
  );
}
